import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import addFormats from 'ajv-formats';
import localize from 'ajv-i18n';
import Ajv2020, { ErrorObject, JSONSchemaType } from 'ajv/dist/2020';
import { filter, map } from 'rxjs';
import {
  Content,
  ContentErrors,
  ContentParseError,
  ContentValidationErrors,
  JSONEditorPropsOptional,
  Mode,
  OnChangeStatus,
  ValidationError,
  ValidationSeverity,
  Validator,
  createJSONEditor,
  isContentParseError,
  isContentValidationErrors
} from 'vanilla-jsoneditor';

import { JsonSchema } from '@core/models/transformers.model';
import { ICONS } from '@core/services/icons/icons.service';
import { extractDefaultsFromSchema } from '@core/utils/json-schema-defaults';
import {
  extractFieldOrderMap,
  sortObjectByFieldOrder
} from '@core/utils/json-schema-utils';
import { isNil } from '@core/utils/shared-utils';

import { AvailableLanguages } from '@shared/types/translation.type';

import { FormMode } from '../row-edition-form.utils';

type JSONEditorInstance = ReturnType<typeof createJSONEditor>;

interface ErrorMessage {
  type: 'parse' | 'validation';
  message: string;
  path?: string;
  line?: number;
  column?: number;
}

@Component({
  selector: 'app-full-json-editor',
  standalone: true,
  imports: [],
  templateUrl: './full-json-editor.component.html',
  styleUrls: ['./full-json-editor.component.css']
})
export class FullJsonEditorComponent implements AfterViewInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  protected readonly ICONS = ICONS;

  jsonSchema = input.required<JsonSchema>();
  initialValues = input<Record<string, unknown>>({});
  formMode = input<FormMode>('create');

  valueChange = output<Record<string, unknown>>();
  validChange = output<boolean>();

  private editorContainer = viewChild<ElementRef<HTMLDivElement>>('editorContainer');
  private editor: JSONEditorInstance | null = null;
  private initialized = signal(false);

  private currentValue = signal<Record<string, unknown>>({});
  private isValid = signal(true);
  private fieldOrderMap = signal<Map<string, number>>(new Map());

  // i18n support
  protected readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang as AvailableLanguages)
    ),
    { initialValue: this.translate.getCurrentLang() as AvailableLanguages }
  );

  // Error state management
  private rawContentErrors = signal<ContentErrors | null | undefined>(null);
  protected errorCount = signal<number>(0);
  protected showAllErrors = signal<boolean>(false);

  // Computed error messages (reactive to language changes)
  protected errorMessages = computed(() => {
    this.currentLang(); // Trigger reactivity
    const errors = this.rawContentErrors();
    if (!errors) return [];

    if (isContentParseError(errors)) {
      return this.formatParseError(errors);
    }
    if (isContentValidationErrors(errors)) {
      return this.formatValidationErrors(errors);
    }
    return [];
  });

  protected visibleErrors = computed(() => {
    const messages = this.errorMessages();
    return this.showAllErrors() ? messages : messages.slice(0, 3);
  });

  protected hasMoreErrors = computed(() => {
    return this.errorMessages().length > 3 && !this.showAllErrors();
  });

  constructor() {
    // React to initialValues changes after editor is initialized
    effect(() => {
      const schema = this.jsonSchema();

      // Extract and store field order map
      const orderMap = extractFieldOrderMap(schema);
      this.fieldOrderMap.set(orderMap);

      const values = this.initialValues();
      const mode = this.formMode();

      if (this.initialized() && this.editor) {
        let contentToSet = values;

        // In create mode with empty values, use schema defaults
        if (mode === 'create' && (!values || Object.keys(values).length === 0)) {
          contentToSet = extractDefaultsFromSchema(schema);
        }

        // Sort content before setting
        contentToSet = sortObjectByFieldOrder(contentToSet, orderMap);

        this.setEditorContent(contentToSet);
      }
    });

    // React to language changes and re-validate with new locale
    effect(() => {
      this.currentLang();

      if (this.initialized() && this.editor) {
        // Trigger re-validation by calling editor's validate method
        // This will re-run our validator with the new language
        this.editor.validate();
      }
    });
  }

  ngAfterViewInit(): void {
    const container = this.editorContainer()?.nativeElement;
    if (!container) return;

    const schema = this.jsonSchema();
    const mode = this.formMode();
    const initialVals = this.initialValues();

    // Extract field order map
    const orderMap = extractFieldOrderMap(schema);
    this.fieldOrderMap.set(orderMap);

    // Determine initial content
    let initialContent: Record<string, unknown>;
    if (mode === 'create' && (!initialVals || Object.keys(initialVals).length === 0)) {
      initialContent = extractDefaultsFromSchema(schema);
    } else {
      initialContent = initialVals || {};
    }

    // Sort initial content
    initialContent = sortObjectByFieldOrder(initialContent, orderMap);

    const validator = this.createValidator(schema);

    const props: JSONEditorPropsOptional = {
      mode: Mode.tree,
      mainMenuBar: true,
      navigationBar: true,
      content: { json: initialContent },
      validator,
      onChange: (
        content: Content,
        _previousContent: Content,
        { contentErrors }: OnChangeStatus
      ) => {
        this.handleChange(content, contentErrors);
      }
    };

    this.editor = createJSONEditor({
      target: container,
      props
    });

    this.currentValue.set(initialContent);
    this.initialized.set(true);

    // Emit initial values
    this.valueChange.emit(initialContent);
    this.validChange.emit(true);
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  /** Get current editor values */
  getCurrentValues(): Record<string, unknown> {
    return this.currentValue();
  }

  /** Check if editor content is valid */
  isContentValid(): boolean {
    return this.isValid();
  }

  private createValidator(schema: JsonSchema): Validator {
    // Create AJV instance with same configuration
    const ajv = new Ajv2020({
      allErrors: true,
      verbose: true,
      strict: false
    });
    addFormats(ajv);

    // Compile schema
    const validate = ajv.compile(schema as JSONSchemaType<unknown>);

    // Return validator function compatible with vanilla-jsoneditor
    return (json: unknown): ValidationError[] => {
      const valid = validate(json);

      if (!valid && validate.errors) {
        // Apply ajv-i18n localization to ErrorObject[]
        localize[this.currentLang()](validate.errors);

        // Convert localized ErrorObject[] to ValidationError[]
        return this.convertAjvErrorsToValidationErrors(validate.errors);
      }

      return [];
    };
  }

  /**
   * Convert AJV ErrorObject[] to vanilla-jsoneditor ValidationError[] AJV errors have
   * already been localized by ajv-i18n at this point
   */
  private convertAjvErrorsToValidationErrors(errors: ErrorObject[]): ValidationError[] {
    return errors.map((error) => ({
      path: this.instancePathToJSONPath(error.instancePath),
      message: error.message || error.keyword,
      severity: ValidationSeverity.error
    }));
  }

  /** Convert AJV instancePath ("/foo/bar") to JSONPath (["foo", "bar"]) */
  private instancePathToJSONPath(instancePath: string): string[] {
    if (!instancePath || instancePath === '') {
      return [];
    }
    // Remove leading slash and split
    return instancePath.substring(1).split('/');
  }

  private handleChange(
    content: Content,
    contentErrors: ContentErrors | null | undefined
  ): void {
    const hasErrors = contentErrors !== null && contentErrors !== undefined;
    const isValid = !hasErrors;

    // Store errors for display
    this.rawContentErrors.set(contentErrors);

    // Update error count
    if (!contentErrors) {
      this.errorCount.set(0);
    } else if (isContentParseError(contentErrors)) {
      this.errorCount.set(1);
    } else if (isContentValidationErrors(contentErrors)) {
      this.errorCount.set(contentErrors.validationErrors.length);
    }

    this.isValid.set(isValid);
    this.validChange.emit(isValid);

    // Extract value from content
    if ('json' in content && content.json !== undefined) {
      const value = content.json as Record<string, unknown>;
      this.currentValue.set(value);
      if (isValid) {
        this.valueChange.emit(value);
      }
    } else if ('text' in content && content.text !== undefined) {
      try {
        const parsed = JSON.parse(content.text) as Record<string, unknown>;
        this.currentValue.set(parsed);
        if (isValid) {
          this.valueChange.emit(parsed);
        }
      } catch (err) {
        console.error('error handling form change:', err);
      }
    }
  }

  private setEditorContent(value: Record<string, unknown>): void {
    if (!this.editor) return;

    // Sort the value before setting
    const sortedValue = sortObjectByFieldOrder(value, this.fieldOrderMap());

    const content: Content =
      sortedValue === null || sortedValue === undefined
        ? { json: {} }
        : { json: sortedValue };

    this.editor.set(content);
    this.currentValue.set(sortedValue || {});
  }

  /** Format parse error to display message */
  private formatParseError(error: ContentParseError): ErrorMessage[] {
    const line = error.parseError.line || 0;
    const column = error.parseError.column || 0;
    const message = this.translate.instant(
      'features.entities.edit.json_editor.error.parse_error',
      {
        line,
        column,
        message: error.parseError.message || ''
      }
    );

    return [
      {
        type: 'parse',
        message,
        line,
        column
      }
    ];
  }

  /** Format validation errors to display messages */
  private formatValidationErrors(errors: ContentValidationErrors): ErrorMessage[] {
    return errors.validationErrors.map((error) => {
      // Convert JSONPath (string[]) to string
      const pathArray = error.path || [];
      const path = pathArray.length === 0 ? '/' : '/' + pathArray.join('/');

      return {
        type: 'validation',
        message: error.message, // Already localized by ajv-i18n!
        path
      };
    });
  }

  /** Toggle show all errors visibility */
  protected toggleShowAllErrors(): void {
    this.showAllErrors.update((value) => !value);
  }
}
